import { Button, HStack, Text, VStack } from "@chakra-ui/react";
import { ChangeEvent, FC, useCallback, useState } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";

import {
  UrlMappingPreviewPanel_Query,
  UrlMappingPreviewPanel_Query$data,
} from "~/src/__relay_artifacts__/UrlMappingPreviewPanel_Query.graphql";
import { DateTimePicker } from "~/src/components/common/datepicker/DateTimePicker";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { Switch } from "~/src/components/common/Switch";
import { TextField } from "~/src/components/common/TextField";

import { UrlMappingPreviewResult } from "./UrlMappingPreviewResult";

type PreviewResult = UrlMappingPreviewPanel_Query$data["resolveUrlMapping"];

type Props = {
  siteSlug: string;
};

export const UrlMappingPreviewPanel: FC<Props> = ({ siteSlug }) => {
  const environment = useRelayEnvironment();
  const [url, setUrl] = useState("");
  const [ref, setRef] = useState("");
  const [title, setTitle] = useState("");
  const [envName, setEnvName] = useState("");
  const [at, setAt] = useState<Date | null>(null);
  // Default ON so "save disabled → test → enable" works without an extra click.
  const [includeInactive, setIncludeInactive] = useState(true);
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handlePreview = useCallback(async () => {
    if (!url.trim()) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchQuery<UrlMappingPreviewPanel_Query>(
        environment,
        graphql`
          query UrlMappingPreviewPanel_Query(
            $url: String!
            $siteSlug: String!
            $ref: String
            $title: String
            $envName: String
            $includeInactive: Boolean
            $at: ISO8601DateTime
          ) {
            resolveUrlMapping(
              url: $url
              siteSlug: $siteSlug
              ref: $ref
              title: $title
              envName: $envName
              includeInactive: $includeInactive
              at: $at
            ) {
              inputUrl
              mappedUrl
              matched
              matchedRuleName
              matchedMatchType
              matchedRuleActive
            }
          }
        `,
        {
          url,
          siteSlug,
          ref,
          title,
          envName: envName || null,
          includeInactive,
          at: at ? at.toISOString() : null,
        }
      ).toPromise();

      if (!data?.resolveUrlMapping) throw new Error("assertion failed");
      setResult(data.resolveUrlMapping);
    } catch {
      setError("プレビューに失敗しました");
      setResult(null);
    } finally {
      setIsLoading(false);
    }
  }, [at, environment, envName, includeInactive, ref, siteSlug, title, url]);

  const handleUrlChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setUrl(event.target.value);
    },
    []
  );

  const handleRefChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setRef(event.target.value);
    },
    []
  );

  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTitle(event.target.value);
    },
    []
  );

  const handleEnvNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEnvName(event.target.value);
    },
    []
  );

  const handleIncludeInactiveChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIncludeInactive(event.target.checked);
    },
    []
  );

  return (
    <VStack align="stretch" spacing="12px">
      <FormControl label="テストURL" required>
        <TextField
          type="text"
          name="previewUrl"
          mode="fill"
          value={url}
          onChange={handleUrlChange}
        />
      </FormControl>
      <FormControl label="Referrer">
        <TextField
          type="text"
          name="previewRef"
          mode="fill"
          value={ref}
          onChange={handleRefChange}
        />
      </FormControl>
      <FormControl label="Title">
        <TextField
          type="text"
          name="previewTitle"
          mode="fill"
          value={title}
          onChange={handleTitleChange}
        />
      </FormControl>
      <FormControl label="環境">
        <TextField
          type="text"
          name="previewEnv"
          mode="fill"
          value={envName}
          onChange={handleEnvNameChange}
          placeholder="空欄 = サーバー環境（例: production, staging）"
        />
      </FormControl>
      <FormControl
        label="基準日時"
        help="開始日時・終了日時の判定に使う時刻です。空欄 = 現在時刻。"
      >
        <DateTimePicker
          value={at}
          onChange={setAt}
          placeholder="空欄 = 現在時刻"
          // Above Chakra Modal (1400); MUI picker default is 1300 so calendar sits behind without this.
          popperZIndex={9999}
        />
      </FormControl>
      <FormControl
        label="無効ルールも含める"
        help="ONにすると有効=OFFのルールもプレビュー対象になります。本番SDKでは無効ルールは適用されません。"
      >
        <Switch
          isChecked={includeInactive}
          onChange={handleIncludeInactiveChange}
        />
      </FormControl>
      <HStack>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={handlePreview}
          isLoading={isLoading}
        >
          テスト実行
        </Button>
      </HStack>
      {error && (
        <Text color="red.500" fontSize="sm">
          {error}
        </Text>
      )}
      {result && (
        <UrlMappingPreviewResult
          inputUrl={result.inputUrl}
          mappedUrl={result.mappedUrl}
          matched={result.matched}
          matchedRuleName={result.matchedRuleName}
          matchedMatchType={result.matchedMatchType}
          matchedRuleActive={result.matchedRuleActive}
        />
      )}
    </VStack>
  );
};
